import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { SkillIcon } from "../ui/SkillIcon";
import { Briefcase, Users, Heart, Globe, Wrench, Github, ExternalLink, ChevronDown, ChevronUp, Award } from "lucide-react";
import { SKILL_CATEGORY_COLORS, type SkillCategory } from "../../../../../db/skillCategories";
import SkillCalculationModal from "../modals/SkillCalculationModal";
import { encodeSkillName, isSkillCertified, getSkillCertifications } from "@/lib/skills";
import { CERTIFICATION_DESIGN } from "@/lib/utils";

interface SkillItem {
  name: string;
  level?: number;
  weight?: number;
}

interface SkillCategoriesDisplayProps {
  skillsByCategory: {
    profession: SkillItem[];
    lead: SkillItem[];
    language: SkillItem[];
    tools: SkillItem[];
  };
  displayMode?: "progress" | "weight"; // progress for company pages, weight for project pages
  showPercentage?: boolean;
  animated?: boolean;
  compactMode?: boolean;
  maxSkillsPerColumn?: number; // Maximum skills to show initially per column
  languagesFromGitHub?: {
    fromGitHub: boolean;
    githubUrl?: string;
  };
  calculationType?: "professional" | "activity" | "company" | "project" | "private" | "combined"; // For info hover explanations
  // Additional props for live calculation examples
  activityId?: number; // For activity calculations
  activities?: any[]; // For company calculations
  showHeader?: boolean; // Whether to show the "Skills & Technologies" header
  enhancedProjects?: any[]; // GitHub-enhanced projects for accurate skill calculations
}

// Helper function to get category metadata
const getCategoryInfo = (category: SkillCategory) => {
  const icons = {
    profession: Briefcase,
    lead: Users,
    language: Globe,
    tools: Wrench,
  };
  
  const titles = {
    profession: 'Professional Skills',
    lead: 'Leadership Skills',
    language: 'Languages',
    tools: 'Tools & Technologies',
  };
  
  return {
    icon: icons[category],
    title: titles[category],
    color: SKILL_CATEGORY_COLORS[category],
  };
};

export default function SkillCategoriesDisplay({ 
  skillsByCategory, 
  displayMode = "progress",
  showPercentage = true,
  animated = true,
  compactMode = false,
  maxSkillsPerColumn = 5, // Default to 5 skills per column
  languagesFromGitHub,
  calculationType = "professional",
  activityId,
  activities,
  showHeader = true,
  enhancedProjects = []
}: SkillCategoriesDisplayProps) {
  // State to track which categories are expanded
  const [expandedCategories, setExpandedCategories] = useState<Set<SkillCategory>>(new Set());
  
  // Toggle expansion for a category
  const toggleExpanded = (category: SkillCategory) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };
  // Get all categories in a consistent order
  const categories: SkillCategory[] = ['profession', 'lead', 'language', 'tools'];
  
  return (
    <div className="space-y-4">
      {/* Skills Section Header - conditionally shown */}
      {showHeader && (
        <div className="flex items-center gap-3 justify-between">
          <h3 className="text-lg font-semibold">Skills & Technologies</h3>
        </div>
      )}
      
      {/* Skills Grid */}
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 ${compactMode ? 'gap-4' : 'gap-6'}`}>
      {categories.map((category, sectionIndex) => {
        const skills = skillsByCategory[category] || [];
        const categoryInfo = getCategoryInfo(category);
        const IconComponent = categoryInfo.icon;
        
        // Determine which skills to show based on maxSkillsPerColumn and expanded state
        const isExpanded = expandedCategories.has(category);
        const shouldLimitSkills = maxSkillsPerColumn && skills.length > maxSkillsPerColumn;
        const displayedSkills = shouldLimitSkills && !isExpanded 
          ? skills.slice(0, maxSkillsPerColumn)
          : skills;
        const hiddenSkillsCount = shouldLimitSkills && !isExpanded 
          ? skills.length - maxSkillsPerColumn 
          : 0;
        
        // Always show all categories, even if empty
        return (
          <motion.div
            key={category}
            initial={animated ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: animated ? 0.4 + sectionIndex * 0.1 : 0 }}
          >
            <Card className="h-full border-2">
              <CardHeader className={compactMode ? "pb-3 bg-gradient-to-b from-primary/5 via-primary/3 to-transparent rounded-t-lg" : "pb-4 bg-gradient-to-b from-primary/5 via-primary/3 to-transparent rounded-t-lg"}>
                <CardTitle className={`flex items-center gap-3 ${compactMode ? 'text-sm' : 'text-lg font-semibold'}`}>
                  <div className={`p-2 rounded-lg bg-primary/10 border border-primary/20`}>
                    <IconComponent className={compactMode ? "h-5 w-5 text-primary" : "h-6 w-6 text-primary"} />
                  </div>
                  <span className="truncate text-foreground font-semibold">{categoryInfo.title}</span>
                  {category === 'language' && languagesFromGitHub?.fromGitHub && languagesFromGitHub.githubUrl && (
                    <a
                      href={languagesFromGitHub.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                      title="Languages fetched from GitHub repository"
                    >
                      <Github className="h-4 w-4" />
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className={compactMode ? "pt-2" : "pt-3"}>
                {skills.length > 0 ? (
                  <div className={compactMode ? "space-y-3" : "space-y-4"}>
                    {displayedSkills.map((skill, index) => (
                      <motion.div
                        key={skill.name}
                        initial={animated ? { opacity: 0, x: -20 } : { opacity: 1, x: 0 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: animated ? 0.1 * index : 0 }}
                        className="space-y-2"
                      >
                        <div className="flex justify-between items-center">
                          <Link href={`/professional/skill/${encodeSkillName(skill.name)}`} className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 cursor-pointer hover:text-primary hover:underline transition-colors min-w-0">
                              <SkillIcon 
                                skillName={skill.name} 
                                size={compactMode ? "sm" : "md"} 
                                showFallback={false}
                                className="shrink-0"
                              />
                              <span className={`font-medium truncate ${compactMode ? 'text-xs' : 'text-sm'}`} title={skill.name}>
                                {skill.name}
                              </span>
                              {/* Certification Badge */}
                              {isSkillCertified(skill.name) && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Award className={`${compactMode ? 'h-3 w-3' : 'h-3 w-3'} ${CERTIFICATION_DESIGN.colors.badge} flex-shrink-0`} />
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <div className="text-xs max-w-xs">
                                      <div className="font-semibold mb-3 text-center">Certified Skill</div>
                                      <div className="flex flex-wrap gap-3 justify-center">
                                        {getSkillCertifications(skill.name)
                                          .sort((a: any, b: any) => new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime())
                                          .map((cert: any) => (
                                          <a
                                            key={cert.id}
                                            href={`/certificate/${cert.id}`}
                                            className="group relative"
                                            title={`${cert.title} (${cert.issuingOrganization})`}
                                          >
                                            <img 
                                              src={cert.logoPath} 
                                              alt={`${cert.title} certification badge`}
                                              className="w-12 h-12 object-contain rounded hover:shadow-md transition-shadow"
                                              onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = 'none';
                                                const fallback = target.nextElementSibling as HTMLElement;
                                                if (fallback) fallback.style.display = 'flex';
                                              }}
                                            />
                                            <div 
                                              className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded flex items-center justify-center hidden"
                                            >
                                              <Award className="h-6 w-6 text-white" />
                                            </div>
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </Link>
                          <div className="shrink-0 ml-1 min-w-[2.5rem]">
                            <SkillCalculationModal
                              skillName={skill.name}
                              category={category}
                              calculationType={calculationType}
                              skillsByCategory={skillsByCategory}
                              activityId={activityId}
                              activities={activities}
                              enhancedProjects={enhancedProjects}
                            >
                              <Badge 
                                variant="secondary" 
                                className={`shrink-0 ${compactMode ? 'text-xs' : 'text-xs'}`}
                              >
                                {displayMode === "progress" && skill.level !== undefined
                                  ? `${skill.level}%`
                                  : displayMode === "weight" && skill.weight !== undefined
                                  ? `${Math.round(skill.weight * 100)}%`
                                  : "N/A"
                                }
                              </Badge>
                            </SkillCalculationModal>
                          </div>
                        </div>
                        <Progress 
                          value={
                            displayMode === "progress" && skill.level !== undefined
                              ? skill.level
                              : displayMode === "weight" && skill.weight !== undefined
                              ? skill.weight * 100
                              : 0
                          } 
                          className={`${compactMode ? "h-1.5" : "h-2"} ${isSkillCertified(skill.name) ? CERTIFICATION_DESIGN.colors.progressBarClass : ""}`} 
                          aria-label={`${skill.name} skill progress: ${
                            displayMode === "progress" && skill.level !== undefined
                              ? `${skill.level}%`
                              : displayMode === "weight" && skill.weight !== undefined
                              ? `${Math.round(skill.weight * 100)}%`
                              : "N/A"
                          }`}
                        />
                      </motion.div>
                    ))}
                    
                    {/* Show More/Less Button */}
                    {shouldLimitSkills && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpanded(category)}
                        className="w-full mt-3 text-xs"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="h-3 w-3 mr-1" />
                            Show Less
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-3 w-3 mr-1" />
                            Show {hiddenSkillsCount} More
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className={`text-muted-foreground ${compactMode ? 'text-xs' : 'text-sm'}`}>
                      No skills in this category
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
      </div>
    </div>
  );
}
